import { STAND_REACH } from "../compile/stand-cells.ts";
import type { FrameMarks } from "../compile/stand-map.ts";

/**
 * The footprint panel's painting of the ground around a frame (ADR-132 decision 7): the four brushes and the three
 * kinds of cell they lay, as plain data the panel holds before it is saved. Cells are `column,rowsUp` keys of the
 * panel's cell grid, counted from the frame's bottom-left; Behind and In front may lie up to `STAND_REACH` cells
 * outside the frame, Blocked only inside it. A cell may be blocked and also Behind or In front, never both Behind and In front. Everything here is pure.
 */

/**
 * The four brushes: Blocked paints cells characters cannot walk on, Behind cells where a character is drawn behind the
 * prop, In front cells where it is drawn in front, and Clear takes a cell back to the base line's rule.
 */
export type StandBrush = "blocked" | "behind" | "front" | "clear";

/**
 * The three kinds of painted cell as the panel holds them.
 */
export interface StandPaint {
  readonly blocked: ReadonlySet<string>;
  readonly behind: ReadonlySet<string>;
  readonly front: ReadonlySet<string>;
}

/**
 * A frame's Behind and In front cells as a draft holds them, as keys.
 */
export interface StandDraft {
  readonly behind: readonly string[];
  readonly front: readonly string[];
}

/**
 * The cell grid of the frame a stroke is painted on, in cells.
 */
export interface StandGrid {
  readonly columns: number;
  readonly rows: number;
}

/**
 * Nothing painted.
 */
export const EMPTY_STAND_PAINT: StandPaint = { blocked: new Set(), behind: new Set(), front: new Set() };

/**
 * A cell key as its cell.
 *
 * @param key `column,rowsUp`.
 * @returns The cell.
 */
function cellOf(key: string): [number, number] {
  const [column, row] = key.split(",").map(Number) as [number, number];
  return [column, row];
}

/**
 * A set's cells in canonical order, rows up and then column, as an override stores them.
 *
 * @param keys The cells as keys.
 * @returns The cells.
 */
export function standCellsOf(keys: ReadonlySet<string>): [number, number][] {
  return [...keys].map(cellOf).sort((a, b) => a[1] - b[1] || a[0] - b[0]);
}

/**
 * Whether a cell lies where the brush may paint it: inside the frame for Blocked, within `STAND_REACH` cells of it for
 * the two drawing brushes.
 *
 * @param cell The cell.
 * @param grid The frame's cells.
 * @param reach How many cells outside the frame the cell may lie.
 * @returns True when the cell may be painted.
 */
function within(cell: readonly [number, number], grid: StandGrid, reach: number): boolean {
  return cell[0] >= -reach && cell[1] >= -reach && cell[0] < grid.columns + reach && cell[1] < grid.rows + reach;
}

/**
 * Paints a stroke's cells with a brush. Collision and drawing are two layers (decision 14): Blocked is the collision layer and
 * leaves Behind and In front as they are, Behind and In front are the drawing layer and each takes the cells from the other
 * only, keeping blocked cells, and Clear, or any brush with the right button (`erase`), takes them from all three. Cells a brush may not
 * reach (outside the frame for Blocked, past `STAND_REACH` for the others) are left out. `kinds` holds the stroke to the
 * kinds it may change, the others kept as they are, so a panel that cannot write Behind and In front never shows them
 * painted or erased.
 *
 * @param paint The paint before the stroke.
 * @param brush The brush.
 * @param covered The cells the stroke covers, as keys.
 * @param erase Whether the stroke erases.
 * @param grid The frame's cells.
 * @param kinds The kinds the stroke may change; all three when absent.
 * @returns The paint after the stroke, the same object when nothing changed.
 */
export function paintStand(paint: StandPaint, brush: StandBrush, covered: readonly string[], erase: boolean, grid: StandGrid, kinds?: ReadonlySet<keyof StandPaint>): StandPaint {
  const clearing = erase || brush === "clear";
  const reach = clearing || brush !== "blocked" ? STAND_REACH : 0;
  const cells = covered.filter((key) => within(cellOf(key), grid, reach));
  if (cells.length === 0) {
    return paint;
  }
  const taken = new Set(cells);
  const without = (set: ReadonlySet<string>): ReadonlySet<string> => (cells.some((key) => set.has(key)) ? new Set([...set].filter((key) => !taken.has(key))) : set);
  const added = (set: ReadonlySet<string>): ReadonlySet<string> => (cells.every((key) => set.has(key)) ? set : new Set([...set, ...cells]));
  const painting = (kind: keyof StandPaint): boolean => !clearing && brush === kind;
  const drawn = (kind: keyof StandPaint): boolean => kind === "behind" || kind === "front";
  const yields = (kind: keyof StandPaint): boolean => clearing || (drawn(kind) && drawn(brush as keyof StandPaint) && brush !== kind);
  const changed = (kind: keyof StandPaint): ReadonlySet<string> => (kinds !== undefined && !kinds.has(kind) ? paint[kind] : painting(kind) ? added(paint[kind]) : yields(kind) ? without(paint[kind]) : paint[kind]);
  const next: StandPaint = { blocked: changed("blocked"), behind: changed("behind"), front: changed("front") };
  return next.blocked === paint.blocked && next.behind === paint.behind && next.front === paint.front ? paint : next;
}

/**
 * Whether two sets hold the same cells.
 *
 * @param a One set.
 * @param b The other.
 * @returns True when they match.
 */
function sameSet(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  return a.size === b.size && [...a].every((key) => b.has(key));
}

/**
 * Whether two paintings say the same.
 *
 * @param a One painting.
 * @param b The other.
 * @returns True when each kind holds the same cells in both.
 */
export function sameStandPaint(a: StandPaint, b: StandPaint): boolean {
  return sameSet(a.blocked, b.blocked) && sameSet(a.behind, b.behind) && sameSet(a.front, b.front);
}

/**
 * A frame's saved Behind and In front cells counted in the panel's cell: marks stored in a coarser cell become the
 * finer cells that cover them.
 *
 * @param marks The frame's marks and the cell they are counted in, or undefined for none.
 * @param cell The panel's cell in pixels.
 * @returns The two sets as keys.
 * @throws RangeError when the marks' cell is not a whole multiple of the panel's.
 */
export function marksAtCell(marks: FrameMarks | undefined, cell: number): { readonly behind: ReadonlySet<string>; readonly front: ReadonlySet<string> } {
  if (marks === undefined) {
    return { behind: new Set(), front: new Set() };
  }
  if (marks.cell < cell || marks.cell % cell !== 0) {
    throw new RangeError(`marks counted in ${marks.cell} px cells cannot be counted in ${cell} px cells`);
  }
  const factor = marks.cell / cell;
  const finer = (cells: readonly (readonly [number, number])[]): ReadonlySet<string> => {
    const out = new Set<string>();
    for (const [column, row] of cells) {
      for (let dy = 0; dy < factor; dy += 1) {
        for (let dx = 0; dx < factor; dx += 1) {
          out.add(`${column * factor + dx},${row * factor + dy}`);
        }
      }
    }
    return out;
  };
  return { behind: finer(marks.behind), front: finer(marks.front) };
}

/**
 * A frame's saved Behind and In front cells shown on a panel's grid whatever cell they are counted in: marks in a
 * coarser or equal cell as `marksAtCell` counts them, and marks in a finer cell (a frame's 4 px marks on a placed
 * prop's 8 px grid) coarsened, each grid cell marked when any of the finer cells it covers is. A grid cell that would
 * hold both marks takes In front, the mark the compile lays last, so no cell is both Behind and In front and a stroke that
 * writes the lists is not refused for it.
 *
 * @param marks The frame's marks and the cell they are counted in, or undefined for none.
 * @param cell The panel's cell in pixels.
 * @returns The two sets as keys, and whether finer marks were coarsened to show them.
 * @throws RangeError when neither cell is a whole multiple of the other.
 */
export function marksOnGrid(marks: FrameMarks | undefined, cell: number): { readonly behind: ReadonlySet<string>; readonly front: ReadonlySet<string>; readonly coarsened: boolean } {
  if (marks === undefined || marks.cell >= cell) {
    return { ...marksAtCell(marks, cell), coarsened: false };
  }
  if (cell % marks.cell !== 0) {
    throw new RangeError(`marks counted in ${marks.cell} px cells cannot be shown on a grid of ${cell} px cells`);
  }
  const factor = cell / marks.cell;
  const coarser = (cells: readonly (readonly [number, number])[]): Set<string> => new Set(cells.map(([column, row]) => `${Math.floor(column / factor)},${Math.floor(row / factor)}`));
  const front = coarser(marks.front);
  const behind = new Set([...coarser(marks.behind)].filter((key) => !front.has(key)));
  return { behind, front, coarsened: marks.behind.length + marks.front.length > 0 };
}
